/**
 * MessageFiles (KAN-809)
 *
 * File attachments of a dialog message (price list, catalog, any type that is
 * neither a picture nor audio): a row with the file name; a click saves the
 * file. The file is served by the authenticated API (KAN-770), so it is
 * fetched with the token (downloadApiFile); an S3 link opens in a new tab.
 */
import { useState } from 'react'
import { FileSpreadsheet, FileText } from 'lucide-react'
import type { MessageAttachment } from '@/types/estimate'
import { cn } from '@/lib/utils'
import { downloadApiFile } from '@/services/api/apiFiles'

const FALLBACK_NAME = 'Файл'
const LOAD_ERROR = 'Не удалось загрузить файл'
const SPREADSHEET_NAME = /\.(xlsx?|xlsm|csv|ods)$/i

/** True for an attachment shown as a file row: has a url, not a picture, not audio. */
function isFileAttachment(item: MessageAttachment): boolean {
  return Boolean(item.url) && item.type !== 'image' && item.type !== 'audio'
}

/** Last part of the url path (no query, no trailing slash); "" when there is none. */
function nameFromUrl(url: string): string {
  const path = url.split(/[?#]/)[0].replace(/\/+$/, '')
  const last = path.slice(path.lastIndexOf('/') + 1)
  try {
    return decodeURIComponent(last)
  } catch {
    return last
  }
}

function fileName(file: MessageAttachment): string {
  return file.name || nameFromUrl(file.url) || FALLBACK_NAME
}

function isSpreadsheet(file: MessageAttachment, name: string): boolean {
  return (
    /spreadsheet|excel|csv/i.test(file.mime || '') || SPREADSHEET_NAME.test(name)
  )
}

interface MessageFilesProps {
  attachments: MessageAttachment[] | undefined
  /** The bubble is dark (blue with white text): the row uses light tones. */
  onDark?: boolean
}

/** File attachments: a download row per file. */
export function MessageFiles({ attachments, onDark = false }: MessageFilesProps) {
  const files = (attachments ?? []).filter(isFileAttachment)
  if (files.length === 0) return null
  return (
    <div className="mb-1 flex flex-col gap-1" data-testid="message-files">
      {files.map((file, index) => (
        <MessageFile key={`${index}:${file.url}`} file={file} onDark={onDark} />
      ))}
    </div>
  )
}

/**
 * One row. While the file is loading the row is aria-disabled and ignores
 * clicks (not `disabled`, so it keeps the focus); a failed load shows a short
 * text in the row, the next click tries again.
 */
function MessageFile({
  file,
  onDark,
}: {
  file: MessageAttachment
  onDark: boolean
}) {
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const name = fileName(file)
  const Icon = isSpreadsheet(file, name) ? FileSpreadsheet : FileText

  const download = async () => {
    if (loading) return
    setLoading(true)
    setFailed(false)
    try {
      await downloadApiFile(file.url, name)
    } catch {
      setFailed(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <button
      type="button"
      onClick={download}
      aria-disabled={loading}
      title={name}
      aria-label={`Скачать файл ${name}`}
      data-testid="message-file"
      className={cn(
        'flex w-64 max-w-full items-center gap-2 rounded-md px-2 py-1.5 text-left',
        'focus-visible:outline-none focus-visible:ring-2',
        'aria-disabled:cursor-wait aria-disabled:opacity-60',
        onDark
          ? 'bg-white/15 hover:bg-white/25 focus-visible:ring-white'
          : 'bg-white hover:bg-gray-50 border border-gray-200 focus-visible:ring-blue-500'
      )}
    >
      <Icon aria-hidden="true" className="h-5 w-5 shrink-0" />
      <span className="min-w-0 flex-1">
        <span className="block truncate">{name}</span>
        {failed && (
          <span
            role="alert"
            className={cn(
              'block text-xs',
              onDark ? 'text-red-100' : 'text-red-600'
            )}
          >
            {LOAD_ERROR}
          </span>
        )}
      </span>
    </button>
  )
}
